<template>
  <v-bottom-sheet
    v-model="drawerOpen"
    inset
    max-width="500"
    content-class="rounded-t-3xl overflow-hidden bg-white shadow-2xl"
    style="z-index: 1000;"
  >
    <div class="flex flex-col max-h-[82vh] sm:max-h-[75vh] bg-white rounded-t-3xl">
      <!-- Drag Handle & Header -->
      <div class="pt-2.5 pb-2.5 px-4 sm:px-5 border-b border-gray-100 flex flex-col shrink-0 bg-gray-50/50">
        <div class="w-9 h-1 bg-gray-300 rounded-full mx-auto mb-2"></div>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <v-icon small color="indigo">mdi-filter-variant</v-icon>
            <h3 class="text-sm sm:text-base font-bold text-gray-800 mb-0">Filter Tasks & Cases</h3>
          </div>
          <v-btn icon x-small class="hover:bg-gray-200" @click="drawerOpen = false">
            <v-icon small color="grey darken-2">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Filter Controls Scrollable Body -->
      <div class="flex-1 overflow-y-auto px-4 sm:px-5 py-3 space-y-3">
        <!-- Sort By Filter -->
        <div v-if="isFilterVisible('sortBy')" class="space-y-1">
          <label class="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">Sort By</label>
          <v-select
            v-model="localFilters.sortBy"
            :items="sortOptions"
            item-text="label"
            item-value="value"
            dense
            outlined
            hide-details
            class="rounded-xl text-xs bg-gray-50/60 custom-filter-select"
          ></v-select>
        </div>

        <!-- Sort Order -->
        <div v-if="isFilterVisible('sortBy')" class="space-y-1">
          <label class="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">Sort Order</label>
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              @click="localFilters.sortOrder = 'asc'"
              class="h-9 px-3 text-xs font-semibold rounded-xl border flex items-center justify-center gap-1.5 transition-all cursor-pointer"
              :class="localFilters.sortOrder === 'asc' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 font-bold' : 'border-gray-200 text-gray-600 bg-gray-50/50 hover:bg-gray-100'"
            >
              <v-icon x-small :color="localFilters.sortOrder === 'asc' ? 'indigo' : 'grey'">mdi-sort-ascending</v-icon>
              Ascending
            </button>
            <button
              type="button"
              @click="localFilters.sortOrder = 'desc'"
              class="h-9 px-3 text-xs font-semibold rounded-xl border flex items-center justify-center gap-1.5 transition-all cursor-pointer"
              :class="localFilters.sortOrder === 'desc' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 font-bold' : 'border-gray-200 text-gray-600 bg-gray-50/50 hover:bg-gray-100'"
            >
              <v-icon x-small :color="localFilters.sortOrder === 'desc' ? 'indigo' : 'grey'">mdi-sort-descending</v-icon>
              Descending
            </button>
          </div>
        </div>

        <!-- Status Filter -->
        <div v-if="isFilterVisible('status')" class="space-y-1">
          <label class="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">Task / Case Status</label>
          <v-select
            v-model="localFilters.status"
            :items="statusOptions"
            item-text="label"
            item-value="value"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
            hide-details
            placeholder="Select Status"
            class="rounded-xl text-xs bg-gray-50/60 custom-filter-select"
          ></v-select>
        </div>

        <!-- Category / Sub-Case Filter -->
        <div v-if="isFilterVisible('subCase') && subCaseOptions.length > 0" class="space-y-1">
          <label class="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">Category / Sub-Case</label>
          <v-select
            v-model="localFilters.caseType"
            :items="subCaseOptions"
            item-text="label"
            item-value="value"
            dense
            outlined
            hide-details
            clearable
            placeholder="All Categories"
            class="rounded-xl text-xs bg-gray-50/60 custom-filter-select"
          ></v-select>
        </div>

        <!-- Outcome / User-defined status -->
        <div v-if="isFilterVisible('outcome') && outcomeOptions.length > 0" class="space-y-1">
          <label class="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">Visit / Case Outcome</label>
          <v-select
            v-model="localFilters.userDefinedCaseStatus"
            :items="outcomeOptions"
            item-text="label"
            item-value="value"
            dense
            outlined
            hide-details
            clearable
            placeholder="Select Outcome"
            class="rounded-xl text-xs bg-gray-50/60 custom-filter-select"
          ></v-select>
        </div>

        <!-- Date Filter -->
        <div v-if="isFilterVisible('date')" class="space-y-1">
          <label class="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">Task Date</label>
          <v-text-field
            v-model="localFilters.taskStartDate"
            type="date"
            dense
            outlined
            hide-details
            clearable
            class="rounded-xl text-xs bg-gray-50/60 custom-filter-select"
          ></v-text-field>
        </div>

        <!-- Assigned To Filter -->
        <div v-if="isFilterVisible('assignedTo')" class="space-y-1">
          <label class="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">Assigned To</label>
          <v-select
            v-model="localFilters.assignedTo"
            :items="assignedToOptions"
            item-text="label"
            item-value="value"
            dense
            outlined
            hide-details
            class="rounded-xl text-xs bg-gray-50/60 custom-filter-select"
          ></v-select>
        </div>

        <!-- Entity / Branch Filter -->
        <div v-if="isFilterVisible('entity') && entityOptions.length > 0" class="space-y-1">
          <label class="text-[11px] font-bold text-gray-600 uppercase tracking-wider block">Entity</label>
          <v-select
            v-model="localFilters.entity"
            :items="entityOptions"
            item-text="label"
            item-value="value"
            dense
            outlined
            hide-details
            clearable
            placeholder="Select Entity"
            class="rounded-xl text-xs bg-gray-50/60 custom-filter-select"
          ></v-select>
        </div>
      </div>

      <!-- Actions Footer (All Right-Aligned) -->
      <div class="p-3.5 px-4 sm:px-5 border-t border-gray-100 flex items-center justify-end gap-2 bg-gray-50/80 shrink-0">
        <v-btn
          text
          small
          color="grey darken-2"
          class="rounded-xl text-xs font-bold h-8.5 px-3 capitalize"
          @click="handleClear"
        >
          <v-icon x-small left>mdi-refresh</v-icon>
          Clear
        </v-btn>
        <v-btn
          outlined
          small
          color="grey"
          class="rounded-xl text-xs font-bold h-8.5 px-3.5 capitalize"
          @click="drawerOpen = false"
        >
          Cancel
        </v-btn>
        <v-btn
          depressed
          small
          :color="$vuetify.theme.currentTheme.employeePrimary"
          class="white--text rounded-xl text-xs font-bold h-8.5 px-4 capitalize"
          @click="handleApply"
        >
          Apply Filters
        </v-btn>
      </div>
    </div>
  </v-bottom-sheet>
</template>

<script>
export default {
  name: 'AppTaskFiltersDrawer',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    appSettings: {
      type: Object,
      default: () => ({})
    },
    allUseCases: {
      type: Array,
      default: () => []
    },
    entities: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localFilters: { ...this.filters },
      sortOptions: [
        { label: 'Created At', value: 'createdAt' },
        { label: 'EMI Amount', value: 'emiAmount' },
        { label: 'Tenure', value: 'tenure' },
        { label: 'POS (Principal Outstanding)', value: 'pos' }
      ],
      statusOptions: [
        { label: 'Open', value: 'OPEN' },
        { label: 'In Progress', value: 'IN_PROGRESS' },
        { label: 'Closed / Completed', value: 'COMPLETED' },
        { label: 'Missed', value: 'MISSED' }
      ],
      assignedToOptions: [
        { label: 'Self', value: 'self' },
        { label: 'Reportees', value: 'reportees' },
        { label: 'Team', value: 'team' }
      ]
    }
  },
  computed: {
    drawerOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    availableFiltersConfig() {
      const customization = this.appSettings?.appDashboardCustomization
      if (customization && Array.isArray(customization.availableFilters)) {
        return customization.availableFilters
      }
      return [
        { key: 'sortBy', visible: true },
        { key: 'status', visible: true },
        { key: 'subCase', visible: true },
        { key: 'outcome', visible: true },
        { key: 'date', visible: true },
        { key: 'assignedTo', visible: true },
        { key: 'entity', visible: true }
      ]
    },
    subCaseOptions() {
      if (!this.allUseCases || !this.allUseCases.length) return []
      return this.allUseCases.map((sc) => ({
        label: sc.name || sc.title || sc,
        value: sc.name || sc._id || sc
      }))
    },
    outcomeOptions() {
      if (!this.allUseCases || !this.allUseCases.length) return []
      let list = []
      this.allUseCases.forEach((sc) => {
        if (sc.userDefinedCaseStatus && Array.isArray(sc.userDefinedCaseStatus)) {
          sc.userDefinedCaseStatus.forEach((st) => {
            const val = typeof st === 'string' ? st : st.name || st.value
            if (val && !list.some((item) => item.value === val)) {
              list.push({ label: val, value: val })
            }
          })
        }
      })
      return list
    },
    entityOptions() {
      if (!this.entities || !this.entities.length) return []
      return this.entities.map((e) => ({
        label: e.entityName || e.name || e.title,
        value: e._id || e.id
      }))
    }
  },
  watch: {
    filters: {
      deep: true,
      handler(newVal) {
        this.localFilters = { ...newVal }
      }
    }
  },
  methods: {
    isFilterVisible(key) {
      const item = this.availableFiltersConfig.find((f) => f.key === key)
      return item ? item.visible !== false : true
    },
    handleApply() {
      this.$emit('apply', { ...this.localFilters })
      this.drawerOpen = false
    },
    handleClear() {
      this.localFilters = {
        sortBy: 'createdAt',
        sortOrder: 'desc',
        assignedTo: 'self',
        caseType: undefined,
        status: [],
        userDefinedCaseStatus: undefined,
        taskStartDate: undefined,
        entity: undefined
      }
      this.$emit('apply', { ...this.localFilters })
      this.drawerOpen = false
    }
  }
}
</script>

<style scoped>
::v-deep .custom-filter-select .v-input__control {
  min-height: 38px !important;
}

::v-deep .custom-filter-select .v-input__slot {
  min-height: 38px !important;
  margin-bottom: 0 !important;
  padding: 0 10px !important;
  border-radius: 12px !important;
}

::v-deep .custom-filter-select .v-select__selections {
  padding: 0 !important;
  font-size: 12px !important;
}

::v-deep .custom-filter-select .v-text-field__details {
  display: none !important;
}
</style>
